import { AgendaSlotType } from '@omecare/utils';
import { CheckIcon, ChevronDownIcon, Star } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PriorityBadge } from '@/components/priority-badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { INSTALLABLE_STAR_CLASSNAME } from '@/lib/tones';
import { cn } from '@/lib/utils';
import { selectDoctorSubstitutesViewModel } from './book-agenda-slot-admin-form.viewmodel';
import { BookAgendaSlotAdminUseForm } from './use-book-agenda-slot-admin-form';

type Props = {
  form: BookAgendaSlotAdminUseForm;
  officeId: string;
  slotType: AgendaSlotType;
  date?: string;
  period?: string;
  onSelectAvailable: (doctorSubstituteId: string) => void;
  bookLoading: boolean;
  pendingInvitationEmail?: string;
};

const BookAgendaSlotAdmin = ({
  form,
  officeId,
  slotType,
  date,
  period,
  onSelectAvailable,
  bookLoading,
  pendingInvitationEmail,
}: Props) => {
  const { t } = useTranslation();
  const doctorSubstitutesViewModel = selectDoctorSubstitutesViewModel({
    officeId,
    slotType,
    date,
    period,
  });
  const [open, setOpen] = useState<boolean>(false);
  const mode = form.watch('mode') ?? 'select';

  const {
    availableSubstitutes,
    unavailableOptions,
    invitedOptions,
    hasAvailabilityGroups,
    loading,
  } = doctorSubstitutesViewModel.data;

  const selectedInvitationId = form.watch('invitationId');

  useEffect(() => {
    if (selectedInvitationId || !pendingInvitationEmail) return;
    const match = invitedOptions.find(
      (o) => o.email === pendingInvitationEmail,
    );
    if (match) {
      form.setValue('invitationId', match.invitationId);
    }
  }, [pendingInvitationEmail, invitedOptions, selectedInvitationId, form]);

  const handleSelectInvited = (invitationId: string) => {
    form.setValue('invitationId', invitationId);
    form.setValue('doctorSubstituteId', undefined);
  };

  return (
    <Form {...form}>
      <form className="grid gap-4">
        <Tabs
          value={mode}
          onValueChange={(tab) =>
            form.setValue('mode', tab as 'select' | 'invite')
          }
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="select">
              {t('book-agenda-slot-admin-form.mode.select')}
            </TabsTrigger>
            <TabsTrigger value="invite">
              {t('book-agenda-slot-admin-form.mode.invite')}
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="select"
            className="grid gap-4"
          >
            <FormField
              control={form.control}
              name="tag"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('view-admin-agenda-slot-sheet.reservation.tag.label')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t(
                        'view-admin-agenda-slot-sheet.reservation.tag.placeholder',
                      )}
                      maxLength={10}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {hasAvailabilityGroups && (
              <div className="grid gap-2">
                <FormLabel>
                  {t(
                    'book-agenda-slot-admin-form.doctor-substitute.group-available',
                  )}
                </FormLabel>
                {loading ? (
                  <div className="flex flex-wrap gap-2">
                    {[0, 1, 2].map((i) => (
                      <Skeleton
                        key={i}
                        className="h-16 w-[180px] rounded-lg"
                      />
                    ))}
                  </div>
                ) : availableSubstitutes.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    {t(
                      'book-agenda-slot-admin-form.doctor-substitute.no-available',
                    )}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {availableSubstitutes.map((sub) => (
                      <button
                        key={sub.id}
                        type="button"
                        disabled={bookLoading}
                        onClick={() => onSelectAvailable(sub.id)}
                        className="text-left disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Card className="flex min-w-[180px] flex-col gap-1 p-3 transition-colors hover:bg-accent focus:bg-accent">
                          <span className="font-medium text-sm">
                            {sub.label}
                          </span>
                          <div className="flex flex-wrap items-center gap-1">
                            {sub.priority !== undefined && (
                              <PriorityBadge priority={sub.priority} />
                            )}
                            {sub.installable === true && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Star
                                    className={cn(
                                      'size-3',
                                      INSTALLABLE_STAR_CLASSNAME,
                                    )}
                                  />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <span>
                                    {t(
                                      'agenda-slot-calendar-event.installable-badge',
                                    )}
                                  </span>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </Card>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <FormField
              control={form.control}
              name="doctorSubstituteId"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                    <FormLabel>
                      {(() => {
                        const hasInvited = invitedOptions.length > 0;
                        if (hasAvailabilityGroups) {
                          return t(
                            hasInvited
                              ? 'book-agenda-slot-admin-form.doctor-substitute.unavailable-select-label-with-invited'
                              : 'book-agenda-slot-admin-form.doctor-substitute.unavailable-select-label',
                          );
                        }
                        return t(
                          hasInvited
                            ? 'book-agenda-slot-admin-form.doctor-substitute.label-with-invited'
                            : 'book-agenda-slot-admin-form.doctor-substitute.label',
                        );
                      })()}
                    </FormLabel>

                    <Popover
                      open={open}
                      onOpenChange={setOpen}
                    >
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            disabled={bookLoading}
                            className={cn(
                              'w-[240px] justify-between',
                              !field.value &&
                                !selectedInvitationId &&
                                'text-muted-foreground',
                            )}
                          >
                            {doctorSubstitutesViewModel.api.getSelectLabel(
                              field.value,
                              selectedInvitationId,
                            )}
                            <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-[240px] p-0">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder={t(
                              'book-agenda-slot-admin-form.doctor-substitute.search-placeholder',
                            )}
                            className="h-9"
                            onValueChange={(value) => {
                              doctorSubstitutesViewModel.api.setSearchDoctorSubstitute(
                                value,
                              );
                            }}
                          />
                          <CommandList>
                            <CommandEmpty>
                              {t(
                                'book-agenda-slot-admin-form.doctor-substitute.no-results',
                              )}
                            </CommandEmpty>
                            <CommandGroup
                              heading={
                                hasAvailabilityGroups
                                  ? t(
                                      'book-agenda-slot-admin-form.doctor-substitute.unavailable-section',
                                    )
                                  : undefined
                              }
                            >
                              {unavailableOptions.map((option) => (
                                <CommandItem
                                  key={option.id}
                                  value={option.label}
                                  className="cursor-pointer text-muted-foreground"
                                  onSelect={() => {
                                    setOpen(false);
                                    form.setValue(
                                      'doctorSubstituteId',
                                      option.value,
                                    );
                                    form.setValue('invitationId', undefined);
                                  }}
                                >
                                  {option.label}
                                  <CheckIcon
                                    className={cn(
                                      'ml-auto h-4 w-4',
                                      option.value === field.value
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                    )}
                                  />
                                </CommandItem>
                              ))}
                            </CommandGroup>

                            {invitedOptions.length > 0 && (
                              <CommandGroup
                                heading={t(
                                  'book-agenda-slot-admin-form.doctor-substitute.invited-section',
                                )}
                              >
                                {invitedOptions.map((option) => (
                                  <CommandItem
                                    key={option.invitationId}
                                    value={option.label}
                                    className="cursor-pointer text-muted-foreground"
                                    onSelect={() => {
                                      setOpen(false);
                                      handleSelectInvited(option.invitationId);
                                    }}
                                  >
                                    {option.label}
                                    <CheckIcon
                                      className={cn(
                                        'ml-auto h-4 w-4',
                                        option.invitationId ===
                                          selectedInvitationId
                                          ? 'opacity-100'
                                          : 'opacity-0',
                                      )}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <FormDescription>
                    {t(
                      'book-agenda-slot-admin-form.doctor-substitute.description',
                    )}
                  </FormDescription>

                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>

          <TabsContent
            value="invite"
            className="grid gap-4"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('invite-doctor-substitute-form.email.label')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t(
                        'invite-doctor-substitute-form.email.placeholder',
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('invite-doctor-substitute-form.first-name.label')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t(
                        'invite-doctor-substitute-form.first-name.placeholder',
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('invite-doctor-substitute-form.last-name.label')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t(
                        'invite-doctor-substitute-form.last-name.placeholder',
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>
        </Tabs>
      </form>
    </Form>
  );
};

export default BookAgendaSlotAdmin;
